import React, { useState, useRef } from 'react';
import { useTransition, animated } from 'react-spring';
import { SPEED_SLOW } from '@nokia-csf-uxr/freeform-design-tokens/tokens/speed';
import { getContext, useCombinedRefs, getDuration, isAnimationDisabled } from 'common';
import Styled from './styles';

const DEFAULT_ANIMATION = immediate => ({
  from: { opacity: 0 },
  enter: { opacity: 1 },
  leave: { opacity: 0 },
  unique: true,
  config: { duration: getDuration(SPEED_SLOW) },
  immediate
});

export interface TabsContentProps extends React.ComponentPropsWithRef<'div'> {
  /** Content is passed through this prop. */
  children: React.ReactNode;
  /** Right-to-left. It's preferred that RTL is toggled at an application level. However if you need a component level override, this prop can help. */
  rtl?: boolean;
  /** Index of selected tab. */
  selected?: number;
  /** Control animation config here. This uses React-spring transitions config. See https://react-spring.io for more information. */
  animationProps?: any;
  /** If true, disable all motion animation for this component.  This property will override any global disableAnimation setting. */
  disableAnimation?: boolean;
}

/* An (optional) Tabs content container to facilitate animations. All props than explicitly defined will be pass to wrapper elements */
const TabsContent = React.forwardRef<HTMLElement, TabsContentProps>((props: TabsContentProps, ref) =>
  getContext(({ RTL, disableAnimation }) => {
    const { children, selected: currentTab, rtl, animationProps, disableAnimation: propsdisableAnimation, ...otherProps } = props;
    const innerRef = useRef(null);
    const containerRef = useCombinedRefs(ref, innerRef);
    const [previousTab, setPreviousTab] = useState(currentTab);
    if (currentTab !== previousTab) {
      setPreviousTab(currentTab);
    }
    const isRTL = rtl || RTL;
    const animations = animationProps || DEFAULT_ANIMATION(isAnimationDisabled(propsdisableAnimation, disableAnimation));
    const transitions = useTransition(currentTab === previousTab, currentTab, {
      ...animations,
    });
    return (
      <Styled.TabsContent rtl={isRTL} ref={containerRef} {...otherProps}>
        {transitions.map(({ item, key, props: transProps }) => {
          // @ts-expect-error: no proper solution found for now
          const newKey = key && (key > 0) ? key : 0;
          return (
            (item !== false) && (
            <animated.div
              key={newKey}
              style={{
                ...transProps,
              }}
            >
              {children[newKey]}
            </animated.div>
            ));
        })}
      </Styled.TabsContent>
    );
  }));

TabsContent.defaultProps = {
  rtl: false,
  selected: undefined,
  animationProps: undefined,
  disableAnimation: undefined,
};

TabsContent.displayName = 'TabsContent';
export default TabsContent;
